import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

type FormState = "idle" | "sending" | "success" | "error";

export default function ContactSection() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [charCount, setCharCount] = useState(0);
  const { t } = useTranslation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormState("sending");
    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) {
        data.append(el.name, el.value);
      }
    }
    try {
      await fetch("https://readdy.ai/api/form/d6vh6tfb245v7h34oog0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setFormState("success");
      form.reset();
      setCharCount(0);
    } catch {
      setFormState("error");
    }
  };

  return (
    <section id="contact" className="bg-[#F8F7F4] py-32 px-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-20 items-start">
          {/* Left: info */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-px bg-[#3D6B5A]" />
              <span
                className="text-[11px] tracking-[0.25em] uppercase text-[#3D6B5A] font-medium"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t("contact.badge")}
              </span>
            </div>
            <h2
              className="text-5xl font-light text-[#1A1A18] leading-tight mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t("contact.title1")}<br />
              <em className="italic">{t("contact.title2")}</em>
            </h2>
            <p
              className="text-[#6B6B65] text-[15px] leading-relaxed font-light mb-12"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t("contact.description")}
            </p>

            <div className="space-y-7">
              <a
                href="mailto:kontakt@exomed.no"
                className="flex items-center gap-5 group cursor-pointer"
                rel="nofollow"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#EBF2EE] rounded-lg group-hover:bg-[#3D6B5A] transition-colors duration-300">
                  <i className="ri-mail-line text-[#3D6B5A] text-xl group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <div
                    className="text-[11px] tracking-[0.2em] uppercase text-[#9B9A94] font-medium mb-0.5"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t("contact.emailLabel")}
                  </div>
                  <div
                    className="text-[#1A1A18] text-[15px] font-light group-hover:text-[#3D6B5A] transition-colors duration-300"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    kontakt@exomed.no
                  </div>
                </div>
              </a>

              <a
                href="tel:51535153"
                className="flex items-center gap-5 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#EBF2EE] rounded-lg group-hover:bg-[#3D6B5A] transition-colors duration-300">
                  <i className="ri-phone-line text-[#3D6B5A] text-xl group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <div
                    className="text-[11px] tracking-[0.2em] uppercase text-[#9B9A94] font-medium mb-0.5"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t("contact.phoneLabel")}
                  </div>
                  <div
                    className="text-[#1A1A18] text-[15px] font-light group-hover:text-[#3D6B5A] transition-colors duration-300"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    51 53 51 53
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-10">
            {formState === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-[#EBF2EE] rounded-full mb-5">
                  <i className="ri-check-line text-[#3D6B5A] text-2xl" />
                </div>
                <h3
                  className="text-2xl font-light text-[#1A1A18] mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {t("contact.successTitle")}
                </h3>
                <p
                  className="text-[#6B6B65] text-[14px] font-light"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t("contact.successDesc")}
                </p>
                <button
                  onClick={() => setFormState("idle")}
                  className="mt-7 text-[#3D6B5A] text-[13px] tracking-widest uppercase font-medium hover:underline cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {t("contact.sendAnother")}
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="exomed-contact-form"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-[11px] tracking-[0.18em] uppercase text-[#9B9A94] font-medium mb-2"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {t("contact.firstName")}
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      className="w-full h-11 px-4 text-sm text-[#1A1A18] bg-[#F8F7F4] border border-[#E8E4DC] rounded-md focus:outline-none focus:border-[#3D6B5A] transition-colors duration-200"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-[11px] tracking-[0.18em] uppercase text-[#9B9A94] font-medium mb-2"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {t("contact.lastName")}
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      className="w-full h-11 px-4 text-sm text-[#1A1A18] bg-[#F8F7F4] border border-[#E8E4DC] rounded-md focus:outline-none focus:border-[#3D6B5A] transition-colors duration-200"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-[11px] tracking-[0.18em] uppercase text-[#9B9A94] font-medium mb-2"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t("contact.emailAddr")}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full h-11 px-4 text-sm text-[#1A1A18] bg-[#F8F7F4] border border-[#E8E4DC] rounded-md focus:outline-none focus:border-[#3D6B5A] transition-colors duration-200"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-[11px] tracking-[0.18em] uppercase text-[#9B9A94] font-medium mb-2"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t("contact.subject")}
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full h-11 px-4 text-sm text-[#1A1A18] bg-[#F8F7F4] border border-[#E8E4DC] rounded-md focus:outline-none focus:border-[#3D6B5A] transition-colors duration-200"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="message"
                      className="block text-[11px] tracking-[0.18em] uppercase text-[#9B9A94] font-medium"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {t("contact.message")}
                    </label>
                    <span
                      className={`text-[11px] font-light ${charCount > 500 ? "text-red-500" : "text-[#9B9A94]"}`}
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 text-sm text-[#1A1A18] bg-[#F8F7F4] border border-[#E8E4DC] rounded-md focus:outline-none focus:border-[#3D6B5A] transition-colors duration-200 resize-none"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                  {charCount > 500 && (
                    <p
                      className="text-red-500 text-[12px] mt-1"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {t("contact.charLimit")}
                    </p>
                  )}
                </div>

                {formState === "error" && (
                  <p
                    className="text-red-500 text-[13px] font-light"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t("contact.errorMsg")}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formState === "sending" || charCount > 500}
                  className="w-full h-12 bg-[#3D6B5A] text-white text-[13px] tracking-widest uppercase font-medium rounded-md hover:bg-[#2F5547] transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {formState === "sending" ? t("contact.sending") : t("contact.send")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
